<template>
  <div class="selector-periodo">
    <span class="selector-periodo__label">Periodo:</span>

    <el-select v-model="preset" size="small" @change="alCambiarPreset" style="width: 155px">
      <el-option label="Últimos 7 días" :value="7"></el-option>
      <el-option label="Últimos 30 días" :value="30"></el-option>
      <el-option label="Últimos 90 días" :value="90"></el-option>
      <el-option label="Personalizado" :value="0"></el-option>
    </el-select>

    <el-date-picker
      v-if="preset === 0"
      v-model="rangoLibre"
      type="daterange"
      size="small"
      unlink-panels
      format="dd/MM/yyyy"
      value-format="yyyy-MM-dd"
      range-separator="a"
      start-placeholder="Desde"
      end-placeholder="Hasta"
      :clearable="false"
      :picker-options="opcionesPicker"
      class="selector-periodo__picker"
      @change="alCambiarRango"
    ></el-date-picker>
  </div>
</template>

<script>
import { DatePicker, Option, Select } from 'element-ui'

/**
 * Selector de periodo comun a todas las pantallas de Foodcourt: presets de
 * 7/30/90 dias mas un rango de fechas libre.
 *
 * Vive en un componente y no copiado en cada vista por la misma razon que
 * `ResuelveRangoDeFechas` es un trait en el backend: las pantallas tienen que
 * poder hablar del MISMO periodo. Si el tope de dias o el manejo de zonas
 * horarias cambia en una y no en las otras, las cifras dejan de ser
 * comparables sin que nada falle visiblemente.
 *
 * Emite `cambio` con { desde, hasta } en formato YYYY-MM-DD, incluida una vez
 * al montarse — por eso las vistas NO deben llamar a su `cargar()` en
 * `created`: se dispararian dos peticiones identicas.
 */
export default {
  name: 'SelectorPeriodo',
  components: {
    [Select.name]: Select,
    [Option.name]: Option,
    [DatePicker.name]: DatePicker
  },
  props: {
    /** Preset inicial en dias. Debe ser uno de los ofrecidos. */
    dias: {
      type: Number,
      default: 30
    }
  },
  data () {
    return {
      // 0 = "Personalizado". Se usa 0 y no null porque el value de
      // el-option con null no se distingue de "sin seleccion".
      preset: this.dias,
      rangoLibre: null,
      // Primera fecha clickeada dentro del picker, para poder limitar el
      // rango mientras se elige la segunda. Ver `opcionesPicker`.
      anclaSeleccion: null
    }
  },
  computed: {
    opcionesPicker () {
      const self = this

      return {
        /*
          Dos limites, los dos por una razon concreta:

          - Futuro: el data lake no tiene dias que no ocurrieron. Dejar
            elegirlos solo produce pantallas vacias sin explicacion.
          - 180 dias: es el tope que aplica `ResuelveRangoDeFechas` en el
            backend. Si no se limita aca, el backend recorta en silencio y la
            pantalla muestra un rango distinto del que el usuario pidio.
        */
        disabledDate (fecha) {
          const hoy = new Date()
          hoy.setHours(23, 59, 59, 999)

          if (fecha > hoy) {
            return true
          }

          if (self.anclaSeleccion) {
            return Math.abs(fecha - self.anclaSeleccion) > (self.TOPE_DIAS - 1) * 86400000
          }

          return false
        },
        onPick (rango) {
          // Con maxDate ya elegido el rango se cerro: se suelta el ancla para
          // que la proxima apertura del picker empiece sin restriccion.
          self.anclaSeleccion = rango.maxDate ? null : rango.minDate
        }
      }
    }
  },
  created () {
    // Mismo tope que el backend. En data() no, para que no sea reactivo.
    this.TOPE_DIAS = 180
    this.emitir(this.rangoDePreset(this.preset))
  },
  methods: {
    /**
     * Fecha local en YYYY-MM-DD.
     *
     * A mano y no con `toISOString()`: ese devuelve UTC, y en Lima (UTC-5)
     * despues de las 19:00 le suma un dia — la pantalla pediria hasta mañana
     * y el "ultimo dia" quedaria corrido.
     */
    aIso (d) {
      const mes = String(d.getMonth() + 1).padStart(2, '0')
      const dia = String(d.getDate()).padStart(2, '0')

      return `${d.getFullYear()}-${mes}-${dia}`
    },
    rangoDePreset (dias) {
      const hasta = new Date()
      const desde = new Date()
      desde.setDate(desde.getDate() - (dias - 1))

      return { desde: this.aIso(desde), hasta: this.aIso(hasta) }
    },
    emitir (rango) {
      this.$emit('cambio', rango)
    },
    alCambiarPreset (valor) {
      if (valor === 0) {
        // Se abre el picker con el rango del preset anterior ya cargado, para
        // que el usuario ajuste en vez de empezar de cero.
        if (!this.rangoLibre) {
          const r = this.rangoDePreset(this.dias)
          this.rangoLibre = [r.desde, r.hasta]
        }

        this.emitir({ desde: this.rangoLibre[0], hasta: this.rangoLibre[1] })

        return
      }

      this.emitir(this.rangoDePreset(valor))
    },
    alCambiarRango (valor) {
      // `clearable` esta desactivado, pero el componente igual puede emitir
      // null si se cierra el picker sin completar el rango.
      if (!valor || !valor[0] || !valor[1]) {
        return
      }

      this.emitir({ desde: valor[0], hasta: valor[1] })
    }
  }
}
</script>

<style scoped>
.selector-periodo {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.selector-periodo__label { font-size: 14px; color: #6b7684; }
.selector-periodo__picker { width: 250px; }

/* El input del daterange trae un ancho fijo de Element que desborda el
   header en pantallas angostas. */
::v-deep .el-date-editor--daterange.el-input__inner { width: 250px; }
</style>
